import { useContext } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { KBContext, SearchContext } from "../../Context";
import searchArticle from "../../images/searchArticle.svg";
import rightIcon from "../../images/rightIcon.svg";
import useNavigateWithSearch from "../../Context/KBContext/hooks/useNavigateWithSearch";
import { KB_WEBSITE_GAINSIGHT_EVENTS, sendGainsightEvent } from "../../gainSightIntegration";

const PageWrapper = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0;
  margin-top: 32px;
  box-sizing: border-box;
  overflow: hidden;
  font-family: Inter, sans-serif;

  @media screen and (max-width: 778px) {
    margin-top: 24px;
  }

  @media screen and (max-width: 500px) {
    margin-top: 16px;
  }
`;

const CountHeader = styled.div`
  font-family: Inter, sans-serif;
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
  color: #1a202c;
  margin: 0 0 4px 0;
`;

const ArticleWrapper = styled.div`
  margin-top: 24px;
  display: grid;
  row-gap: 24px;
  width: 100%;
`;

const ArticleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  gap: 16px;
  padding: 20px;
  background: var(--slate-50, #f8fafc);
  border: 1px solid var(--slate-200, #e2e8f0);
  border-radius: 12px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04);
    background: #fff;
    border-color: var(--slate-200, #e2e8f0);
  }
`;

const ArticleImageContainer = styled.div`
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  cursor: inherit;
`;

const Article = styled.div`
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  cursor: inherit;
`;

const ArtcleHeader = styled.label`
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 28px;
  letter-spacing: 0;
  color: #0f172a;
  cursor: inherit;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;

  &.searchTitle > *,
  mark {
    font-family: Inter, sans-serif;
    font-weight: 500;
    font-size: 18px;
    line-height: 28px;
    letter-spacing: 0;
    color: #0f172a;
  }
`;

const ArticleBody = styled.label`
  font-family: Inter, sans-serif;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #64748b;
  display: -webkit-box;
  cursor: inherit;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  margin: 0;

  .searchContent > *,
  strong,
  mark {
    font-family: Inter, sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    color: #64748b;
  }
`;

const ArticleHeaderContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 6px;
  width: 100%;
  overflow: hidden;
  cursor: inherit;
`;

const CategoryTitle = styled.label`
  font-family: Inter, sans-serif;
  border: 1px solid var(--slate-200, #e2e8f0);
  box-sizing: border-box;
  border-radius: 6px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  color: #64748b;
  padding: 4px 10px;
  cursor: inherit;
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
`;

const BreadCrumContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 0 0 20px 0;
  font-family: Inter, sans-serif;

  @media screen and (max-width: 500px) {
    margin-bottom: 16px;
  }
`;

const BreadCrum = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #475569;
  margin-right: 8px;
  cursor: pointer;
`;

const RightImage = styled.img`
  height: 9.55px;
  width: 5.83px;
  margin-right: 8px;
`;

const SubmitIssueText = styled.span`
  font-family: Inter, sans-serif;
  color: ${(props) => props.color};
  font-weight: 500;
  cursor: pointer;
`;

const NoResults = styled.div`
  font-family: Inter, sans-serif;
  margin-top: 16px;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #64748b;
`;

const ImageContainer = styled.div`
  width: 64px;
  height: 64px;
  background-image: url(${searchArticle});
  background-repeat: no-repeat;
  background-position: center;
  background-color: #D6E0EB;
  border-radius: 50%;
  cursor: inherit;
`;

function App() {
  const { searchList, setSearchInput, searchInput, setSearchList } =
    useContext(SearchContext);
  const { data } = useContext(KBContext);
  const { pathname } = useLocation();
  const navigate = useNavigateWithSearch();


  const renderCount = () => {
    if (searchList.length === 0) {
      return <>No results for "{searchInput}"</>;
    } else if (searchList.length === 1) {
      return <>{searchList.length} article found</>;
    } else {
      return <>{searchList.length} articles found</>;
    }
  };

  function extractContent(s, space) {
    var span = document.createElement("span");
    span.innerHTML = s;
    if (space) {
      var children = span.querySelectorAll("*");
      for (var i = 0; i < children.length; i++) {
        if (children[i].textContent) children[i].textContent += " ";
        else children[i].innerText += " ";
      }
    }
    return [span.textContent || span.innerText].toString().replace(/ +/g, " ");
  }

  const splitMatchedText = (str, content) => {
    const regExp = new RegExp(str, "gi");
    return content.replace(regExp, (match) => `<mark>${match}</mark>`);
  };

  const handleSearchResultClick = (item, index) => {
    sendGainsightEvent(KB_WEBSITE_GAINSIGHT_EVENTS.KB_SEARCH_RESULT_CLICKED, {
      search_query: searchInput,
      clicked_article_slug: item.slug,
      clicked_article_title: extractContent(item?.title, true),
      result_position: index + 1,
    });

    setSearchInput("");
    navigate(`/article/${item.slug}`);
  };

  return (
    <PageWrapper bannerLeft={pathname.slice(1) === ""}>
      <BreadCrumContainer>
        <BreadCrum
          onClick={() => {
            setSearchInput("");
            setSearchList([]);
            navigate("/");
          }}
        >
          Home
        </BreadCrum>
        <RightImage src={rightIcon} alt="righticon" />
        <BreadCrum>{"Search results"}</BreadCrum>
      </BreadCrumContainer>
      <CountHeader>{renderCount()}</CountHeader>
      {searchList?.length === 0 && (
        <NoResults>
          {data?.enableHelpForm ? (
            <>
              Please{" "}
              <SubmitIssueText
                color={data.brandColor}
                onClick={() => navigate("/submit-issue")}
              >
                submit an Issue
              </SubmitIssueText>{" "}
              if you can’t find what you are looking for
            </>
          ) : (
            <>We couldn’t find what you are looking for.</>
          )}
        </NoResults>
      )}
      <ArticleWrapper>
        {searchList?.map((item, index) => {

          return (
            <ArticleContainer
              key={item.articleId}
              onClick={() => handleSearchResultClick(item, index)}
            >
              <ArticleImageContainer>
                <ImageContainer />
              </ArticleImageContainer>
              <Article>
                <ArticleHeaderContainer>
                  <ArtcleHeader
                    className="searchTitle"
                    dangerouslySetInnerHTML={{
                      __html: splitMatchedText(
                        searchInput,
                        extractContent(item?.title, true)
                      ),
                    }}
                  />
                  {item?.categoryTitle && <CategoryTitle>{item.categoryTitle}</CategoryTitle>}
                </ArticleHeaderContainer>
                <ArticleHeaderContainer>
                  <ArticleBody
                    className="searchContent"
                    dangerouslySetInnerHTML={{
                      __html: splitMatchedText(
                        searchInput,
                        extractContent(item?.content, true)
                      ),
                    }}
                  />
                </ArticleHeaderContainer>
              </Article>
            </ArticleContainer>
          );
        })}
      </ArticleWrapper>
    </PageWrapper>
  );
}

export default App;
